import { useEffect } from "react";

import {
  NAVER_AD_ID,
  NAVER_INFLOW_DOMAIN,
  NAVER_AD_TEST_MODE,
} from "common/constants";

function appendScript(code) {
  const el = document.createElement("script");
  el.type = "text/javascript";
  el.text = code;
  document.body.appendChild(el);
  return el;
}

/**
 * 네이버 광고 유입/전환 추적 훅 (wcs.trans 기반 신버전 API)
 *
 * 사용 예시
 * - 단순 유입:        useNaverAnalytics()
 * - 콘텐츠 조회 전환: useNaverAnalytics({ type: "view_content" })
 * - 회원가입 전환:    useNaverAnalytics({ type: "sign_up" })
 * - 상품 상세 전환:   useNaverAnalytics(
 *     { type: "view_product", items: [{ id: "상품ID", name: "상품명", category: "카테고리", quantity: "1", payAmount: "가격", option: "옵션명" }] },
 *     { enabled: !!productId }
 *   )
 * - 결제 시작 전환:   useNaverAnalytics(
 *     {
 *       type: "begin_checkout", // (필수) items는 선택
 *       items: [
 *         { id: "A 상품ID", name: "A 상품명", payAmount: "A 상품가격" },
 *         { id: "B 상품ID", name: "B 상품명", payAmount: "B 상품가격" },
 *       ],
 *     },
 *     { enabled: !!items.length }
 *   )
 * - 구매 전환:        useNaverAnalytics(
 *     {
 *       type: "purchase",   // (필수)
 *       value: totalAmount, // (필수) 총 구매액
 *       orderId: orderNo,   // 주문 번호 (선택)
 *       items: [            // 선택, 복수 상품 시 각각 포함
 *         { id: "A 상품ID", name: "A 상품명", payAmount: "A 상품가격" },
 *         { id: "B 상품ID", name: "B 상품명", payAmount: "B 상품가격" },
 *       ],
 *     },
 *     { enabled: !!totalAmount }
 *   )
 *
 * items 필드 (전환 유형별 권장 구조)
 * - view_product:   { id(필수), name, category, quantity, payAmount, option }
 * - begin_checkout: { id, name, payAmount } — type만 필수, items 전체 생략 가능
 * - purchase:       { id, name, payAmount } — type·value 필수, orderId·items 선택
 *
 * @param {{ type: string, value?: number|string, orderId?: string, items?: Array }} [conversion]
 * @param {{ enabled?: boolean, deps?: Array }} [options] - enabled: false이면 전환 스크립트 미실행 (비동기 데이터 대기용) / deps: 추가 의존성 (예: route params 변경 시 재실행)
 */
function useNaverAnalytics(conversion, options = {}) {
  const { enabled = true } = options;

  // PV 유입 스크립트 — 마운트 시 항상 1회 실행
  // 가이드: wcs.inflow(도메인) → wcs_do() 순서
  //
  // [문제] SPA(React Router)에서 document.referrer는 실제 페이지 리로드가 없으면 갱신되지 않음
  //        → WCS SDK가 document.referrer를 읽어 e 필드를 세팅하므로 e 값이 항상 고정됨
  // [해결] wcs_add["e"]에 직전 페이지 URL을 직접 주입하여 SDK의 document.referrer 읽기를 덮어씀
  //        → 페이지 방문 시마다 현재 URL을 sessionStorage(wcs_prev_url)에 저장,
  //           다음 페이지에서 이를 꺼내 e 값으로 사용
  //
  // [카카오 로그인] 리다이렉트 전에 저장해 둔 URL(wcs_referrer_before_kakao)을 최우선 사용
  useEffect(() => {
    // wcs_referrer_before_kakao 존재 여부 우선 확인 (document.referrer가 kakao.com을 포함하지 않는 경우 대비)
    const storedRef = sessionStorage.getItem("wcs_referrer_before_kakao");
    if (storedRef) sessionStorage.removeItem("wcs_referrer_before_kakao");

    const isFromKakao = !!storedRef || document.referrer.includes("kakao.com");

    const prevUrl = sessionStorage.getItem("wcs_prev_url");
    // 우선순위: 카카오 리다이렉트 전 URL > SPA 직전 페이지 URL > 없으면 미설정(document.referrer 기본값 유지)
    const refUrl = storedRef || prevUrl || "";

    // 카카오 OAuth 콜백 페이지 감지 (wcs_referrer_before_kakao 존재 또는 referrer=kakao.com, AND URL에 ?code= 포함)
    // 콜백 URL은 실제 사용자 페이지가 아니므로 PV 추적을 완전히 건너뜀.
    // wcs_prev_url에 콜백 URL이 기록되면 다음 페이지의 e(이전 페이지) 값이 오염되므로,
    // 리다이렉트 이전 URL을 복원해 유지한다.
    const isKakaoCallback =
      isFromKakao && /[?&]code=/.test(window.location.search);
    if (isKakaoCallback) {
      const preKakaoUrl = storedRef || prevUrl || "";
      if (preKakaoUrl) sessionStorage.setItem("wcs_prev_url", preKakaoUrl);
      return; // 콜백 URL PV 추적 건너뜀
    }

    // 현재 페이지 URL을 다음 페이지의 e(이전 페이지) 값으로 저장
    sessionStorage.setItem("wcs_prev_url", window.location.href);

    // WCS SDK는 wcs_do() 호출 시 document.referrer를 직접 읽어 e 필드를 세팅함
    // → wcs_add["e"] 주입 불가, document.referrer를 일시적으로 덮어써야 함
    // wcs_do() 호출 전에 own property로 override → 호출 후 delete로 복원 (prototype getter로 fallback)
    const overridePart = refUrl
      ? `try{Object.defineProperty(document,'referrer',{configurable:true,get:function(){return "${refUrl}";}});}catch(ex){}`
      : "";
    const restorePart = refUrl
      ? `try{delete document.referrer;}catch(ex){}`
      : "";

    const el = appendScript(
      `if(window.wcs){if(!wcs_add){var wcs_add={};}wcs_add["wa"]="${NAVER_AD_ID}";${overridePart}wcs.inflow("${NAVER_INFLOW_DOMAIN}");wcs_do();${restorePart}}`,
    );
    return () => {
      if (document.body.contains(el)) document.body.removeChild(el);
    };
  }, []);

  // 전환 이벤트 스크립트 — conversion 있고 enabled=true일 때만 실행
  // 가이드: wcs.trans(_conv) 방식 (신버전)
  useEffect(() => {
    if (!conversion || !enabled) return;

    const { type: rawType, value, orderId, items } = conversion;
    const type = NAVER_AD_TEST_MODE ? `test_${rawType}` : rawType;
    const valuePart = value !== undefined ? `_conv.value="${value}";` : "";
    const idPart = orderId ? `_conv.id="${orderId}";` : "";
    const itemsPart = items ? `_conv.items=${JSON.stringify(items)};` : "";

    const el = appendScript(
      `if(window.wcs){if(!wcs_add){var wcs_add={};}wcs_add["wa"]="${NAVER_AD_ID}";var _conv={};_conv.type="${type}";${valuePart}${idPart}${itemsPart}wcs.trans(_conv);}`,
    );
    return () => {
      if (document.body.contains(el)) document.body.removeChild(el);
    };
  }, [enabled]);
}

/**
 * 명령형 전환 이벤트 전송 — hook 외부(콜백 등)에서 호출 가능
 * @param {{ type: string, value?: number|string, orderId?: string, items?: Array }} conversion
 */
export function sendNaverConversion({ type: rawType, value, orderId, items }) {
  const type = NAVER_AD_TEST_MODE ? `test_${rawType}` : rawType;
  const valuePart = value !== undefined ? `_conv.value="${value}";` : "";
  const idPart = orderId ? `_conv.id="${orderId}";` : "";
  const itemsPart = items ? `_conv.items=${JSON.stringify(items)};` : "";
  appendScript(
    `if(window.wcs){if(!wcs_add){var wcs_add={};}wcs_add["wa"]="${NAVER_AD_ID}";var _conv={};_conv.type="${type}";${valuePart}${idPart}${itemsPart}wcs.trans(_conv);}`,
  );
}

export default useNaverAnalytics;
